import axios from "axios";
import { rootUrl } from "../rootUrl";

export interface Client {
  id: string;
  organization: string;
  logo: string;
  email: string;
  ein: string;
}

export interface Campaign {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  clientId: string;
  logo: string;
  name: string;
  description: string;
  goalAmount: number;
  expires?: Date | null;
  tagline?: string | null;
  socialShareImage?: string | null;
  alertTitle?: string | null;
  alertDescription?: string | null;
  alertImage?: string | null;
  alertButtonLabel?: string | null;
  colorTheme?: string | null;
  sponsorBonusMultiple?: number | null;
  maxBonus?: number | null;
  hideDonationAmounts?: boolean;
  isAdminCreated: boolean;
  scheduledMessages?: ScheduledMessage[];
  client?: {
    id: string;
    organization: string;
    logo: string;
  };
}

export interface ScheduledMessage {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  campaignId: string;
  messageText: string;
  scheduledFor: Date;
  status: "PENDING" | "SENT" | "FAILED" | "CANCELLED";
  socialShareImage?: string | null;
}

export interface CreateCampaignRequest {
  clientId: string;
  ein?: string;
  beneficiaryName?: string;
  logo: string;
  name: string;
  description: string;
  goalAmount: number;
  expires?: string;
  tagline?: string;
  socialShareImage?: string;
  alertTitle?: string;
  alertDescription?: string;
  alertImage?: string;
  alertButtonLabel?: string;
  colorTheme?: string;
  sponsorBonusMultiple?: number;
  maxBonus?: number;
  hideDonationAmounts?: boolean;
}

export interface UpdateCampaignRequest {
  logo?: string;
  name?: string;
  description?: string;
  goalAmount?: number;
  expires?: string;
  tagline?: string;
  socialShareImage?: string;
  alertTitle?: string;
  alertDescription?: string;
  alertImage?: string;
  alertButtonLabel?: string;
  colorTheme?: string;
  sponsorBonusMultiple?: number;
  maxBonus?: number;
  hideDonationAmounts?: boolean;
}

export interface ScheduledMessageRequest {
  messageText: string;
  scheduledFor: string;
  socialShareImage?: string;
}

export interface RecipientCountResponse {
  totalContacts: number;
  alreadyDonated: number;
  reachableCount: number;
}

export interface CampaignListItem {
  id: string;
  name: string;
  createdAt: Date;
  expires?: Date | null;
  goalAmount: number;
}

const buildAuthHeaders = (token: string) => ({
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

export const getClients = async (token: string): Promise<Client[]> => {
  const { data } = await axios.get(`${rootUrl}/admin/clients`, buildAuthHeaders(token));
  return data;
};

export const getCampaignsByClient = async (
  token: string,
  clientId: string
): Promise<CampaignListItem[]> => {
  const { data } = await axios.get(
    `${rootUrl}/admin/clients/${clientId}/campaigns`,
    buildAuthHeaders(token)
  );
  return data;
};

export const createCampaign = async (
  token: string,
  campaign: CreateCampaignRequest
): Promise<Campaign> => {
  const { data } = await axios.post(
    `${rootUrl}/admin/campaign/create`,
    campaign,
    buildAuthHeaders(token)
  );
  return data;
};

export const getCampaign = async (token: string, id: string): Promise<Campaign> => {
  const { data } = await axios.get(
    `${rootUrl}/admin/campaign/${id}`,
    buildAuthHeaders(token)
  );
  return data;
};

export const updateCampaign = async (
  token: string,
  id: string,
  updates: UpdateCampaignRequest
): Promise<Campaign> => {
  const { data } = await axios.patch(
    `${rootUrl}/admin/campaign/${id}`,
    updates,
    buildAuthHeaders(token)
  );
  return data;
};

export const getRecipientCount = async (
  token: string,
  campaignId: string
): Promise<RecipientCountResponse> => {
  const { data } = await axios.get(
    `${rootUrl}/admin/campaign/${campaignId}/recipient-count`,
    buildAuthHeaders(token)
  );
  return data;
};

export const createScheduledMessage = async (
  token: string,
  campaignId: string,
  message: ScheduledMessageRequest
): Promise<any> => {
  const { data } = await axios.post(
    `${rootUrl}/admin/campaign/${campaignId}/message`,
    message,
    buildAuthHeaders(token)
  );
  return data;
};

export const updateScheduledMessage = async (
  token: string,
  campaignId: string,
  messageId: string,
  message: ScheduledMessageRequest
): Promise<any> => {
  const { data } = await axios.patch(
    `${rootUrl}/admin/campaign/${campaignId}/messages/${messageId}`,
    message,
    buildAuthHeaders(token)
  );
  return data;
};

export const deleteScheduledMessage = async (
  token: string,
  campaignId: string,
  messageId: string
): Promise<any> => {
  const { data } = await axios.delete(
    `${rootUrl}/admin/campaign/${campaignId}/messages/${messageId}`,
    buildAuthHeaders(token)
  );
  return data;
};

export const deleteCampaign = async (
  token: string,
  campaignId: string
): Promise<any> => {
  const { data } = await axios.delete(
    `${rootUrl}/admin/campaign/${campaignId}`,
    buildAuthHeaders(token)
  );
  return data;
};

export interface ClientAsset {
  name: string;
  url: string;
  createdAt: string;
}

export const getClientAssets = async (
  token: string,
  clientId: string
): Promise<ClientAsset[]> => {
  const { data } = await axios.get(
    `${rootUrl}/admin/clients/${clientId}/assets`,
    buildAuthHeaders(token)
  );
  return data;
};

export const deleteClientAsset = async (
  token: string,
  clientId: string,
  url: string
): Promise<any> => {
  const { data } = await axios.delete(
    `${rootUrl}/admin/clients/${clientId}/assets`,
    {
      ...buildAuthHeaders(token),
      data: { url }
    }
  );
  return data;
};

export interface SendTestMessageRequest {
  campaignId: string;
  messageText: string;
  testPhoneNumber: string;
  socialShareImage?: string;
}

export interface SendTestMessageResponse {
  success: boolean;
  message: string;
  to: string;
}

export const sendTestMessage = async (
  token: string,
  request: SendTestMessageRequest
): Promise<SendTestMessageResponse> => {
  const { data } = await axios.post(
    `${rootUrl}/message/test`,
    request,
    buildAuthHeaders(token)
  );
  return data;
};
